import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, DatePicker, Select, Button, Space, Tooltip, Empty } from 'antd';
import { HistoryOutlined, ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { getJobHistory } from 'api/jobHistoryApi';
import { JobHistoryItem, JobHistoryActionLabels, JobFieldLabels } from 'types/jobHistory';
import { YYYY_MM_DD } from 'constants/commons';
import styled from 'styled-components';
import type { RangeValue } from 'rc-picker/lib/interface';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface JobHistoryProps {
  jobId: string;
}

const JobHistory: React.FC<JobHistoryProps> = ({ jobId }) => {
  const { t } = useTranslation(['recruitment', 'common', 'action']);
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState<JobHistoryItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dateRange, setDateRange] = useState<RangeValue<Moment>>(null);
  const [selectedAction, setSelectedAction] = useState<string | undefined>(undefined);
  const [availableActions, setAvailableActions] = useState<string[]>([]);

  const fetchJobHistory = async () => {
    if (!jobId) return;

    setLoading(true);
    try {
      const filters = {
        page: currentPage,
        limit: pageSize,
        fromDate:
          dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD HH:mm:ss') : undefined,
        toDate: dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD HH:mm:ss') : undefined,
        action: selectedAction,
      };

      console.log('Calling API with jobId:', jobId, 'filters:', filters);
      const response = await getJobHistory(jobId, filters);
      console.log('API response:', response);

      // Xử lý response data
      let historyArray = [];
      if (Array.isArray(response)) {
        historyArray = response;
      } else if (response.data && Array.isArray(response.data)) {
        historyArray = response.data;
      } else if (response.result && Array.isArray(response.result)) {
        historyArray = response.result;
      }

      console.log('History array:', historyArray);
      setHistoryData(historyArray);
      setTotalRecords(historyArray.length);
    } catch (error) {
      console.error('Error fetching job history:', error);
      setHistoryData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobHistory();
  }, [jobId, currentPage, pageSize, dateRange, selectedAction]);

  const formatChanges = (changes: any) => {
    if (!changes || typeof changes !== 'object') return null;

    return Object.entries(changes).map(([field, change]: [string, any]) => {
      const fieldLabel = JobFieldLabels[field as keyof typeof JobFieldLabels] || field;

      return (
        <div key={field} style={{ marginBottom: 4 }}>
          <strong>{fieldLabel}:</strong>
          <span style={{ color: '#ff4d4f', textDecoration: 'line-through', marginLeft: 8 }}>
            {change.oldValue || 'N/A'}
          </span>
          <span style={{ margin: '0 8px' }}>→</span>
          <span style={{ color: '#52c41a' }}>{change.newValue || 'N/A'}</span>
        </div>
      );
    });
  };
  const fmt = (v?: number | string) => {
    return (v || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const columns = [
    {
      title: t('No.'),
      key: 'index',
      width: 60,
      render: (_: any, __: JobHistoryItem, index: number) => index + 1,
    },

    {
      title: t('common:time'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => (
        <Tooltip title={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
          {moment(date).format('DD/MM/YYYY HH:mm')}
        </Tooltip>
      ),
    },
    {
      title: t('recruitment:position_name'),
      dataIndex: 'positionName',
      key: 'positionName',
      width: 200,
    },
    {
      title: t('recruitment:headcount'),
      dataIndex: 'headcount',
      key: 'headcount',
      width: 100,
    },
    {
      title: t('recruitment:salary_range'),
      key: 'salary',
      width: 200,
      render: (record: JobHistoryItem) => {
        if (record.minimumSalary || record.maximumSalary) {
          const min = fmt(record.minimumSalary);
          const max = fmt(record.maximumSalary);
          return `${min} - ${max} ${record.currencySymbol || 'VND'}`;
        }
        return 'N/A';
      },
    },
    {
      title: t('recruitment:location'),
      dataIndex: 'location',
      key: 'location',
      width: 150,
      render: (location: any) => {
        if (!location) return '—';

        // Nếu location là object có thuộc tính name
        if (typeof location === 'object' && location.name) {
          return location.name;
        }

        // Nếu location là string
        if (typeof location === 'string') {
          return location;
        }

        // Fallback
        return '—';
      },
    },
    {
      title: t('recruitment:department'),
      dataIndex: 'department',
      key: 'department',
      width: 150,
      render: (department: any) => {
        if (!department) return '—';

        // Nếu department là object có thuộc tính name
        if (typeof department === 'object' && department.name) {
          return department.name;
        }

        // Nếu department là string
        if (typeof department === 'string') {
          return department;
        }

        // Fallback
        return '—';
      },
    },
    {
      title: t('Job ID'),
      dataIndex: 'jobId',
      key: 'jobId',
      width: 150,
      render: (value?: string) => value ?? '—',
    },

    {
      title: t('Job Type'),
      dataIndex: 'jobType',
      key: 'jobType',
      width: 150,
      render: (jobType: any) => {
        if (!jobType) return '—';

        // Nếu location là object có thuộc tính name
        if (typeof jobType === 'object' && jobType.name) {
          return jobType.name;
        }

        // Nếu location là string
        if (typeof jobType === 'string') {
          return jobType;
        }

        // Fallback
        return '—';
      },
    },
  ];

  return (
    <Card
      title={
        <Space>
          <HistoryOutlined />
          {t('JOB HISTORY')}
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchJobHistory} loading={loading}>
            {t('action:refresh')}
          </Button>
        </Space>
      }
    >
      <FilterSection></FilterSection>

      <Table
        columns={columns}
        dataSource={historyData}
        rowKey="id"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalRecords,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size || 10);
          },
        }}
        locale={{
          emptyText: (
            <Empty
              description={t('recruitment:no_history_data')}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />
    </Card>
  );
};

export default JobHistory;

const FilterSection = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  background: #fafafa;
  border-radius: 6px;
`;
